import type { Role, SignalClientMsg, SignalServerMsg } from "./types";

export interface SignalEvents {
  onOpen?: () => void;
  onReady?: () => void;
  onPeerLeft?: () => void;
  onFull?: () => void;
  onExpired?: () => void;
  onCodeTaken?: () => void;
  onSdp?: (sdp: RTCSessionDescriptionInit) => void;
  onIce?: (candidate: RTCIceCandidateInit) => void;
  onRestart?: () => void;
  onClose?: () => void;
  onError?: (err: Event) => void;
}

const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 8000, 8000];

export class SignalChannel {
  private target: string;
  private ws: WebSocket | undefined;
  private closed = false;
  private attempt = 0;
  private retryTimer: number | undefined;

  constructor(
    url: string,
    private code: string,
    private role: Role,
    private cid: string,
    private events: SignalEvents
  ) {
    const base = `${url.replace(/\/$/, "")}/room/${code}`;
    const params = new URLSearchParams({ role });
    if (cid) params.set("cid", cid);
    this.target = `${base}?${params.toString()}`;
    this.connect();
  }

  private connect(): void {
    if (this.closed) return;
    const ws = new WebSocket(this.target);
    this.ws = ws;
    ws.addEventListener("open", () => {
      this.attempt = 0;
      ws.send(JSON.stringify({ type: "hello", role: this.role } satisfies SignalClientMsg));
      this.events.onOpen?.();
    });
    ws.addEventListener("message", (ev) => this.handle(ev));
    ws.addEventListener("close", () => {
      // After explicit close() the channel is stale — its callbacks are
      // closures over an old session. Firing onClose here would let an
      // async close event poke the new session's state machine
      // (e.g. scheduleReconnecting flipping a freshly-paired connection
      // back into "reconnecting").
      if (this.closed) return;
      this.events.onClose?.();
      this.scheduleRetry();
    });
    ws.addEventListener("error", (e) => {
      if (this.closed) return;
      this.events.onError?.(e);
    });
  }

  private scheduleRetry(): void {
    if (this.closed) return;
    const delay = RETRY_DELAYS_MS[this.attempt] ?? RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1]!;
    this.attempt += 1;
    if (this.attempt > RETRY_DELAYS_MS.length) {
      this.closed = true;
      return;
    }
    this.retryTimer = window.setTimeout(() => this.connect(), delay);
  }

  private handle(ev: MessageEvent): void {
    if (this.closed) return;
    if (typeof ev.data !== "string") return;
    let msg: SignalServerMsg;
    try {
      msg = JSON.parse(ev.data) as SignalServerMsg;
    } catch {
      return;
    }
    switch (msg.type) {
      case "ready":
        this.events.onReady?.();
        break;
      case "peer-left":
        this.events.onPeerLeft?.();
        break;
      case "full":
        this.events.onFull?.();
        break;
      case "expired":
        this.events.onExpired?.();
        break;
      case "code-taken":
        this.events.onCodeTaken?.();
        break;
      case "sdp":
        this.events.onSdp?.(msg.sdp);
        break;
      case "ice":
        this.events.onIce?.(msg.candidate);
        break;
      case "restart":
        this.events.onRestart?.();
        break;
    }
  }

  send(msg: SignalClientMsg): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  close(): void {
    this.closed = true;
    if (this.retryTimer !== undefined) clearTimeout(this.retryTimer);
    this.retryTimer = undefined;
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING)
    ) {
      this.ws.close();
    }
  }
}
